import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {DndContext, DragOverlay} from '@dnd-kit/core';
import * as css from './TimetablePage.css';
import TimetableToolbar from './components/timetableToolbar/TimetableToolbar';
import TimetableGrid from './components/timetableGrid/TimetableGrid';
import LectureCard from './components/lectureCard/LectureCard';
import {getTimetable} from '../../redux/timetable/timetableActions';
import {RootState} from '../../redux/store.type';
import {useTimetableDays} from './hooks/useTimetableDays';
import {useTimeGroups} from './hooks/useTimeGroups';
import {useDragLecture} from './hooks/useDragLecture';
import PageWrapper from '../components/pageWrapper/PageWrapper';
import Loader from '../../component/loader/Loader';
import ViewToggle from '../../assets/icons/ViewToggle';
import PlusThin from '../../assets/icons/PlusThin';
import ConferenceDates from '../../assets/icons/ConferenceDates';
import DownloadReport from '../../assets/icons/DownloadReport';
import useShow from '../../hooks/useShow';
import ManageLecturePopup from './components/manageLecturePopup/ManageLecturePopup';

function TimetablePage() {
    const {t} = useTranslation('timetable');
    const navigate = useNavigate();
    const eid = useSelector((state: RootState) => state.event.eid);
    const lecturesByDay = useSelector((state: RootState) => state.timetable.lectures);
    const [expanded, setExpanded] = useState(true);

    const days = useTimetableDays();
    const currentDayXHR = useSelector((state: RootState) => state.timetable.getLecturesXHR[days.currentDay?.timestamp]);
    const lectures = lecturesByDay[days.currentDay?.timestamp] ?? [];
    const timeGroups = useTimeGroups(lectures, days.currentDay);
    const drag = useDragLecture(lectures);
    const lectureFormShow = useShow();

    useEffect(() => {
        if(days.currentDay?.timestamp) getTimetable(days.currentDay.timestamp);
    }, [days.currentDay?.timestamp]);

    const buttons = [
        {key: 0, children: <ViewToggle />, onClick: () => setExpanded(e => !e), withoutPlus: true, variant: 'subtle'},
        {key: 1, children: <DownloadReport />, text: t('downloadLecturesReport'), onClick: () => {}, withoutPlus: true, variant: 'neutral', disabled: true},
        {key: 2, children: <ConferenceDates />, text: t('conferenceDates'), onClick: () => navigate(`/e/${eid}/event`), withoutPlus: true, variant: 'neutral'},
        {key: 'divider', Comp: () => <css.toolbarDivider />},
        {key: 3, children: <PlusThin />, text: t('addItem'), onClick: lectureFormShow.open, withoutPlus: true, variant: 'neutral'},
        {key: 4, children: <PlusThin />, text: t('addLecture'), onClick: lectureFormShow.open, withoutPlus: true, variant: 'brand'},
    ];
    const dateNav = <TimetableToolbar />;
    const routing = {rightContent: dateNav};
    const mobileActions = [
        {key: 5, onClick: days.onPrevDay, text: t('prevDay'), withoutPlus: true},
        {key: 6, text: days.currentDateLabel, withoutPlus: true, list: {text: days.currentDateLabel, arr: days.conferenceDays.map((day: {key: string; label: string}, i: number) => ({text: day.label, key: String(i), onClick: () => days.onDaySelect(i)}))}},
        {key: 7, onClick: days.onNextDay, text: t('nextDay'), withoutPlus: true},
        {key: 8, onClick: () => setExpanded(e => !e), text: t('toggleView'), withoutPlus: true},
        {key: 9, onClick: lectureFormShow.open, text: t('addLecture'), withoutPlus: true},
        {key: 10, onClick: lectureFormShow.open, text: t('addItem'), withoutPlus: true},
        {key: 11, onClick: () => navigate(`/e/${eid}/event`), text: t('conferenceDates'), withoutPlus: true},
        {key: 12, onClick: () => {}, text: t('downloadLecturesReport'), withoutPlus: true},
    ];

    const isLoading = currentDayXHR === 'LOADING' && lectures.length === 0;

    return (
        <>
            {/* @ts-expect-error JS component has no TS types */}
            <PageWrapper operations={{buttons, isNewButton: true}} mobileActions={mobileActions} routing={routing} subMenuProps={{wrapCssStyle: '&.changeCss { justify-content: space-between; }'}}>
                <DndContext sensors={drag.sensors} onDragStart={drag.handleDragStart} onDragEnd={drag.handleDragEnd}>
                    <css.container>
                        {isLoading
                            ? <Loader XHR="LOADING" height="100%">{null}</Loader>
                            : (
                                <css.contentArea>
                                    <TimetableGrid
                                        timeGroups={timeGroups}
                                        expanded={expanded}
                                        handlers={{
                                            onAddLecture: () => lectureFormShow.open(),
                                            onAddItem: () => {},
                                        }}
                                    />
                                </css.contentArea>
                            )
                        }
                    </css.container>
                    <DragOverlay>
                        {drag.activeLecture && (
                            <css.dragOverlayCard $isActive={!!drag.activeLecture}>
                                <LectureCard lecture={drag.activeLecture} expanded={expanded} />
                            </css.dragOverlayCard>
                        )}
                    </DragOverlay>
                </DndContext>
            </PageWrapper>
            <ManageLecturePopup show={lectureFormShow} />
        </>
    );
}

export default TimetablePage;
